import { supabase } from './supabase';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export class ApiError extends Error {
  constructor(status, detail) { super(detail); this.status = status; }
}

async function token() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token;
}

export async function request(path, { method = 'GET', body, auth = true, form } = {}) {
  const headers = {};
  if (auth) {
    const t = await token();
    if (t) headers.Authorization = `Bearer ${t}`;
  }
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 60000);
  let res;
  try { res = await fetch(`${BASE}${path}`, { method, headers, signal: controller.signal, body: form || (body !== undefined ? JSON.stringify(body) : undefined) }); }
  catch (e) { throw new ApiError(0, e.name === 'AbortError' ? 'The request timed out. Check your last saved revision before retrying.' : 'Could not reach the server. Check your connection and retry.'); }
  finally { clearTimeout(timer); }
  if (res.status === 204) return null;
  const isJson = res.headers.get('content-type')?.includes('application/json');
  const data = isJson ? await res.json() : await res.text();
  if (!res.ok) throw new ApiError(res.status, (isJson && data?.detail) || 'Something went wrong. Please try again.');
  return data;
}

export const api = {
  health: () => request('/health', { auth: false }),
  templates: () => request('/templates', { auth: false }),
  template: (slug) => request(`/templates/${slug}`, { auth: false }),
  demo: (slug, granularity = 'monthly', override) => request(`/templates/${slug}/demo?granularity=${granularity}${override ? `&override_key=${override.key}&override_value=${override.value}` : ''}`, { auth: false }),
  sampleUrl: (slug) => `${BASE}/templates/${slug}/sample.xlsx`,
  importTemplateUrl: () => `${BASE}/import-template.csv`,
  me: () => request('/me'),
  patchMe: (body) => request('/me', { method: 'PATCH', body }),
  deleteMe: () => request('/me', { method: 'DELETE' }),
  exportMyData: () => request('/me/export'),
  projects: () => request('/projects'),
  project: (id) => request(`/projects/${id}`),
  createProject: (body) => request('/projects', { method: 'POST', body }),
  patchProject: (id, body) => request(`/projects/${id}`, { method: 'PATCH', body }),
  duplicateProject: (id) => request(`/projects/${id}/duplicate`, { method: 'POST' }),
  deleteProject: (id) => request(`/projects/${id}`, { method: 'DELETE' }),
  versions: (id) => request(`/projects/${id}/versions`),
  restoreVersion: (id, version, revision) => request(`/projects/${id}/versions/${version}/restore`, { method: 'POST', body: { revision } }),
  results: (id, granularity = 'monthly') => request(`/projects/${id}/results?granularity=${granularity}`),
  exportProject: (id, scenario) => request(`/projects/${id}/export`, { method: 'POST', body: { scenario } }),
  importPreview: (id, file) => { const form = new FormData(); form.append('file', file); return request(`/projects/${id}/import/preview`, { method: 'POST', form }); },
  importConfirm: (id, body) => request(`/projects/${id}/import/confirm`, { method: 'POST', body }),
  importSheet: (id, importId, sheet) => request(`/projects/${id}/import/${importId}/sheet`, { method: 'POST', body: { sheet } }),
  deleteImport: (id, importId) => request(`/projects/${id}/import/${importId}`, { method: 'DELETE' }),
  enquiry: (body) => request('/enquiries', { method: 'POST', body }),
  adminSummary: () => request('/admin/summary'),
  adminOperations: () => request('/admin/operations'),
  adminEnquiries: () => request('/admin/enquiries'),
  adminEnquiryStatus: (id, status) => request(`/admin/enquiries/${id}`, { method: 'PATCH', body: { status } }),
};
